import './HowToCreate.scss'

import React from 'react'

import { Box, Card, Carousel, CarouselSlide, Column, FluidImage, GridContainer, H2, H3, Hidden, P, ProgressiveImage, Row, Typography, Visible } from '@vp/swan'

import { FeatureGroupModel, FeatureModel, HeroModel } from '@vp/digital-common-data-model-lib-ubik'

import MissingContentAlert from '../../../common/MissingContentAlert'

type HowToCreateProps = {
  content: HeroModel
  accessibility: FeatureGroupModel
}

const HowToCreate = ({ content, accessibility }: HowToCreateProps): JSX.Element => {
  if (!content) {
    return <MissingContentAlert />
  }

  return (
    <GridContainer>
      <Row>
        <Column span={12} darkMode textAlign="center">
          <P m={0} textColor="accent" fontSkin="title-item" fontWeight={{ xs: 'normal', md: 'bold' }}>
            {content.title}
          </P>

          <H2 m={0} mt={4} textColor="standard" fontSkin="title-section" fontSize="x2large" id="how-to-create-a-website">
            {content.description}
          </H2>
        </Column>
      </Row>

      <Visible xs>
        <Row py={6} standardMode>
          <Column span={12} px={3}>
            <Carousel
              initialSlide={0}
              slidesToShow={1.15}
              arrows={false}
              dots
              slidesToScroll={1}
              gridGutters
              dotsClass="slick-dots how-to-create__dots-wrapper"
              className="how-to-create__slider-wrapper"
              accessibleTextForNext={accessibility.features[4].title}
              accessibleTextForPrevious={accessibility.features[3].title}
              aria-roledescription={accessibility.features[0].title}
              aria-label={content.description}
              accessibleTextForDots={
                content?.heroFeatureGroup?.features.length > 0
                  ? content?.heroFeatureGroup?.features.map(
                      (feature, index) => accessibility.features[5]?.title.replace('{{SLIDE_NUMBER}}', String(index + 1)) ?? '',
                    )
                  : []
              }
              accessibleTextForVisibleSlides={accessibility.features[2]?.valueText}
              accessibleTextForSlide={accessibility.features[2]?.altText}
            >
              {content.heroFeatureGroup.features.map((feature: FeatureModel, index: number) => (
                <CarouselSlide
                  as={Card}
                  key={`cardSlide_${index + 1}`}
                  className="wix-websites-pdp__slide"
                  aria-roledescription={accessibility.features[1].title}
                >
                  <ProgressiveImage
                    placeholder={feature.icons[0].placeholderUrl}
                    srcSet={feature.icons[0].resourceSetUrl}
                    src={feature.icons[0].secureUrl}
                    sizes="(max-width: 768px) 100vw, 542px"
                  >
                    {(image) => <FluidImage alt="" srcSet={image.srcSet} src={image.src} sizes={image.sizes} className="how-to-create__card-thumbnail" />}
                  </ProgressiveImage>

                  <Box mt={{ xs: 6 }} textAlign="left">
                    <Typography fontSkin="title-subsection">{feature.title}</Typography>

                    <P fontSkin="body-small" mt={3}>
                      {feature.description}
                    </P>
                  </Box>
                </CarouselSlide>
              ))}
            </Carousel>
          </Column>
        </Row>
      </Visible>

      <Hidden xs>
        <Row as="ul" mt={8} standardMode aria-labelledby="how-to-create-a-website">
          {content.heroFeatureGroup.features.map((feature, index) => (
            <Column as="li" key={`step_${index + 1}`} span={3} spanXs={12}>
              <Card evenHeight textAlign="left">
                <ProgressiveImage
                  placeholder={feature.icons[0].placeholderUrl}
                  srcSet={feature.icons[0].resourceSetUrl}
                  src={feature.icons[0].secureUrl}
                  sizes="(max-width: 2168px) 25vw, 542px"
                >
                  {(image) => <FluidImage alt="" srcSet={image.srcSet} src={image.src} sizes={image.sizes} className="how-to-create__card-thumbnail" />}
                </ProgressiveImage>

                <H3 fontSkin="title-subsection" fontSize="standard" mt={5}>
                  {feature.title}
                </H3>

                <P mb={0} fontSkin="body-small" className="how-to-create__card-description">
                  {feature.description}
                </P>
              </Card>
            </Column>
          ))}
        </Row>
      </Hidden>
    </GridContainer>
  )
}

export default HowToCreate
